import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { clearPitchReturn, peekPitchReturn, rememberPitchReturn } from "@/lib/pitch-tour-partner";
import { beginPortalTransition, endPortalTransition, hardNavigate } from "@/lib/portal-transition";
import { getUtmData, clearUtmData } from "@/hooks/use-utm";
import { analyticsVisitorFields } from "@/lib/visitor-link";
import { analytics, trackConversion } from "@/lib/analytics";
import type { SafeUser } from "@shared/schema";

interface ImpersonatingAgentSummary {
  id: string;
  companyName: string;
  contactEmail: string;
  contactFirstName?: string | null;
  contactLastName?: string | null;
}

// Server-enriched payload returned by /api/auth/me. Extends SafeUser with
// impersonation state and terms-acceptance flag added server-side.
interface AuthMePayload extends SafeUser {
  isImpersonating?: boolean;
  adminId?: string | null;
  adminName?: string | null;
  isImpersonatingAgent?: boolean;
  impersonatingAgent?: ImpersonatingAgentSummary | null;
  /** Task #1421 — true when the user has no acceptance row for the
   * current terms version, surfaced by GET /api/auth/me. */
  needsTermsAcceptance?: boolean;
  previewingProjectId?: string | null;
  previewingProjectTitle?: string | null;
  previewingProjectSlug?: string | null;
}

interface AuthContextType {
  user: SafeUser | null;
  isLoading: boolean;
  isImpersonating: boolean;
  adminId: string | null;
  adminName: string | null;
  isImpersonatingAgent: boolean;
  impersonatingAgent: ImpersonatingAgentSummary | null;
  /** Task #1421 — true when the authenticated non-admin user has no
   * acceptance row for the current terms version. Always false for
   * admins and unauthenticated visitors. */
  needsTermsAcceptance: boolean;
  /** Preview mode: set when an admin is previewing a project as an investor. */
  previewingProjectId: string | null;
  previewingProjectTitle: string | null;
  previewingProjectSlug: string | null;
  login: (email: string, password: string) => Promise<SafeUser>;
  register: (email: string, password: string, name: string, country: string, firstName?: string, lastName?: string, userType?: "investor" | "attorney" | "broker_dealer" | "foreign_agent", extras?: Record<string, unknown>) => Promise<SafeUser>;
  logout: () => Promise<void>;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  startAdminImpersonation: (userId: string) => Promise<void>;
  stopAdminImpersonation: () => Promise<void>;
  startAgentImpersonation: (agentId: string, pitchReturn?: string) => Promise<void>;
  stopAgentImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: rawUser, isLoading } = useQuery<AuthMePayload | null>({
    queryKey: ["/api/auth/me"],
    staleTime: Infinity,
  });

  const user: SafeUser | null = rawUser ?? null;
  const isImpersonating = rawUser?.isImpersonating === true;
  const adminId = rawUser?.adminId ?? null;
  const adminName = rawUser?.adminName ?? null;
  const isImpersonatingAgent = rawUser?.isImpersonatingAgent === true;
  const impersonatingAgent: ImpersonatingAgentSummary | null = rawUser?.impersonatingAgent ?? null;
  // Task #1421 — true only for non-admin authenticated users missing an
  // acceptance row for the current terms version (surfaced by /api/auth/me).
  const needsTermsAcceptance: boolean =
    rawUser?.needsTermsAcceptance === true && rawUser?.role !== "admin";
  const previewingProjectId: string | null = rawUser?.previewingProjectId ?? null;
  const previewingProjectTitle: string | null = rawUser?.previewingProjectTitle ?? null;
  const previewingProjectSlug: string | null = rawUser?.previewingProjectSlug ?? null;

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, role: user.role });
      Sentry.setTag("role", user.role);
      if (user.role === "user") {
        analytics.identify(user.id, {
          email: user.email,
          country: user.country,
          pipelineStage: user.pipelineStage,
          accreditationStatus: user.accreditationStatus,
        });
      }
    } else if (user === null) {
      Sentry.setUser(null);
      Sentry.setTag("role", null);
    }
  }, [user]);

  // Reset the React Query cache on every identity change so the new
  // user can't briefly see the previous user's data. Seed the auth
  // observers first, then drop everything else; keep the auth keys so
  // observers stay subscribed.
  const resetCacheForIdentity = async (next: SafeUser | null) => {
    await queryClient.cancelQueries();
    queryClient.setQueryData(["/api/auth/me"], next);
    queryClient.setQueryData(["/api/agent/me"], null);
    queryClient.removeQueries({
      predicate: (q) => {
        const key = q.queryKey[0];
        return key !== "/api/auth/me" && key !== "/api/agent/me";
      },
    });
  };

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const res = await apiRequest("POST", "/api/auth/login", { email, password, ...analyticsVisitorFields() });
      return res.json() as Promise<SafeUser>;
    },
    onSuccess: async (data) => {
      await resetCacheForIdentity(data);
    },
  });

  const registerMutation = useMutation({
    mutationFn: async ({ email, password, name, country, firstName, lastName, userType, utmSource, utmMedium, utmCampaign, utmTerm, utmContent, referrer, landingPage, termsAccepted, accreditationAcknowledged, marketingOptIn, referralCode }: { email: string; password: string; name: string; country: string; firstName?: string; lastName?: string; userType?: "investor" | "attorney" | "broker_dealer" | "foreign_agent"; utmSource?: string; utmMedium?: string; utmCampaign?: string; utmTerm?: string; utmContent?: string; referrer?: string; landingPage?: string; termsAccepted?: boolean; accreditationAcknowledged?: boolean; marketingOptIn?: boolean; referralCode?: string }) => {
      const res = await apiRequest("POST", "/api/auth/register", { email, password, firstName: firstName || name, lastName: lastName || "", country, userType, utmSource, utmMedium, utmCampaign, utmTerm, utmContent, referrer, landingPage, termsAccepted, accreditationAcknowledged, marketingOptIn, referralCode, ...analyticsVisitorFields() });
      return res.json() as Promise<SafeUser>;
    },
    onSuccess: async (data) => {
      await resetCacheForIdentity(data);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      clearPitchReturn();
      await apiRequest("POST", "/api/auth/logout", {});
    },
    onSuccess: async () => {
      await resetCacheForIdentity(null);
    },
  });

  const login = async (email: string, password: string) => {
    return loginMutation.mutateAsync({ email, password });
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    country: string,
    firstName?: string,
    lastName?: string,
    userType?: "investor" | "attorney" | "broker_dealer" | "foreign_agent",
    extras?: Record<string, unknown>,
  ) => {
    const utm = getUtmData();
    const result = await registerMutation.mutateAsync({ email, password, name, country, firstName, lastName, userType, ...utm, ...extras });
    trackConversion("signup", { method: "email", country, userType, ...utm });
    clearUtmData();
    return result;
  };

  const logout = async () => {
    return logoutMutation.mutateAsync();
  };

  // Cover the screen, set the impersonation cookie, then hard-navigate.
  // Do not null or refetch auth in this document: a cleared identity
  // re-renders the admin/investor gates ("Admin Access Required",
  // "Sign In Required") before the destination boots, which is the
  // flash. The next document loads the new identity under the compass.
  const handoff = async (destination: string, run: () => Promise<unknown>) => {
    beginPortalTransition(destination);
    try {
      await run();
      hardNavigate(destination);
    } catch (err) {
      endPortalTransition();
      throw err;
    }
  };

  const startImpersonation = (userId: string) =>
    handoff("/dashboard", async () => {
      clearPitchReturn();
      await apiRequest("POST", `/api/admin/impersonate/${userId}`, {});
    });

  const stopImpersonation = () =>
    handoff("/admin/investors", () => apiRequest("POST", "/api/admin/stop-impersonate", {}));

  const startAdminImpersonation = (userId: string) =>
    handoff("/admin", async () => {
      clearPitchReturn();
      await apiRequest("POST", `/api/admin/impersonate-admin/${userId}`, {});
    });

  const stopAdminImpersonation = () =>
    handoff("/admin/team", () => apiRequest("POST", "/api/admin/stop-impersonate-admin", {}));

  const startAgentImpersonation = (agentId: string, pitchReturn?: string) =>
    handoff("/agent/dashboard", async () => {
      clearPitchReturn();
      await apiRequest("POST", `/api/admin/impersonate-agent/${agentId}`, {});
      if (pitchReturn) rememberPitchReturn(pitchReturn);
    });

  const stopAgentImpersonation = () => {
    const returnTo = peekPitchReturn() ?? "/admin/agents";
    return handoff(returnTo, async () => {
      await apiRequest("POST", "/api/admin/stop-impersonate-agent", {});
      clearPitchReturn();
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isImpersonating,
        adminId,
        adminName,
        isImpersonatingAgent,
        impersonatingAgent,
        needsTermsAcceptance,
        previewingProjectId,
        previewingProjectTitle,
        previewingProjectSlug,
        login,
        register,
        logout,
        startImpersonation,
        stopImpersonation,
        startAdminImpersonation,
        stopAdminImpersonation,
        startAgentImpersonation,
        stopAgentImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
